/* eslint-disable react-refresh/only-export-components -- node view lives next to its extension */
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import { Info, Lightbulb, TriangleAlert, OctagonAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// A callout (admonition) block: a tinted box with an icon for notes, tips,
// warnings and danger notices.
//
// Storage markup (from renderHTML), which becomes the article's content_html:
//   <div data-callout="info" class="callout callout-info"><p>…</p></div>
//
// The reader (folge-cloud) styles `.callout-*` and draws the icon with CSS, so
// the stored HTML stays plain and sanitizer-friendly.

export type CalloutType = 'info' | 'tip' | 'warning' | 'danger';

export const CALLOUT_TYPES: {
  type: CalloutType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { type: 'info', label: 'Info', icon: Info },
  { type: 'tip', label: 'Tip', icon: Lightbulb },
  { type: 'warning', label: 'Warning', icon: TriangleAlert },
  { type: 'danger', label: 'Danger', icon: OctagonAlert },
];

const isCalloutType = (value: unknown): value is CalloutType =>
  CALLOUT_TYPES.some((c) => c.type === value);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      /** Wrap the current block in a callout of the given type. */
      setCallout: (attrs?: { type?: CalloutType }) => ReturnType;
      /** Wrap in / unwrap from a callout. */
      toggleCallout: (attrs?: { type?: CalloutType }) => ReturnType;
      /** Change the type of the callout around the selection. */
      setCalloutType: (type: CalloutType) => ReturnType;
    };
  }
}

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      type: {
        default: 'info',
        parseHTML: (element) => {
          const value = element.getAttribute('data-callout');
          return isCalloutType(value) ? value : 'info';
        },
        renderHTML: (attributes) => ({
          'data-callout': attributes.type,
          class: `callout callout-${attributes.type}`,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-callout]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setCallout:
        (attrs) =>
        ({ commands }) =>
          commands.wrapIn(this.name, { type: attrs?.type ?? 'info' }),
      toggleCallout:
        (attrs) =>
        ({ commands }) =>
          commands.toggleWrap(this.name, { type: attrs?.type ?? 'info' }),
      setCalloutType:
        (type) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { type }),
    };
  },

  addKeyboardShortcuts() {
    return {
      // Enter on an empty trailing paragraph leaves the callout instead of
      // growing it, so authors can keep typing below the box.
      Enter: ({ editor }) => {
        const { state } = editor;
        const { $from, empty } = state.selection;
        if (!empty) return false;

        let calloutDepth = -1;
        for (let depth = $from.depth; depth > 0; depth--) {
          if ($from.node(depth).type.name === this.name) {
            calloutDepth = depth;
            break;
          }
        }
        if (calloutDepth === -1) return false;

        const paragraph = $from.parent;
        const callout = $from.node(calloutDepth);
        const paragraphIsEmpty = paragraph.type.name === 'paragraph' && paragraph.content.size === 0;
        const paragraphIsLast = $from.index(calloutDepth) === callout.childCount - 1;
        if (!paragraphIsEmpty || !paragraphIsLast || callout.childCount < 2) return false;

        return editor.commands.command(({ tr, dispatch }) => {
          if (!dispatch) return true;
          const paragraphFrom = $from.before($from.depth);
          const paragraphTo = $from.after($from.depth);
          const afterCallout = $from.after(calloutDepth) - paragraph.nodeSize;
          tr.delete(paragraphFrom, paragraphTo);
          tr.insert(afterCallout, state.schema.nodes.paragraph.create());
          tr.setSelection(TextSelection.near(tr.doc.resolve(afterCallout + 1)));
          return true;
        });
      },
      // Backspace at the start of an empty, single-paragraph callout removes the box.
      Backspace: ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty || $from.parentOffset !== 0) return false;
        const parentDepth = $from.depth - 1;
        if (parentDepth < 1) return false;
        const callout = $from.node(parentDepth);
        if (callout.type.name !== this.name) return false;
        if (callout.childCount !== 1 || $from.parent.content.size !== 0) return false;
        return editor.commands.lift(this.name);
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(CalloutView);
  },
});

// ---- Node view (editor UX only; does not affect stored HTML) ---------------

function CalloutView({ node, updateAttributes }: NodeViewProps) {
  const { t } = useTranslation();
  const type: CalloutType = isCalloutType(node.attrs.type) ? node.attrs.type : 'info';
  const current = CALLOUT_TYPES.find((c) => c.type === type) ?? CALLOUT_TYPES[0];
  const Icon = current.icon;

  const typeLabels: Record<CalloutType, string> = {
    info: t('editor.callout.typeInfo'),
    tip: t('editor.callout.typeTip'),
    warning: t('editor.callout.typeWarning'),
    danger: t('editor.callout.typeDanger'),
  };

  return (
    <NodeViewWrapper className={cn('callout-wrapper', `callout-wrapper-${type}`)} data-callout={type}>
      <div className="callout-icon" contentEditable={false} aria-hidden>
        <Icon className="h-4 w-4" />
      </div>
      <NodeViewContent className="callout-content" />
      <div className="callout-type-picker" contentEditable={false} role="group" aria-label={t('editor.callout.groupLabel')}>
        {CALLOUT_TYPES.map((option) => (
          <button
            key={option.type}
            type="button"
            title={typeLabels[option.type]}
            aria-label={typeLabels[option.type]}
            aria-pressed={option.type === type}
            className={cn('callout-type-btn', option.type === type && 'is-active')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => updateAttributes({ type: option.type })}
          >
            <option.icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
    </NodeViewWrapper>
  );
}
